import moment from 'moment'
import { modals } from 'Rentlio/screen/layout/utils/modalsEnum'
import {
  REMOVE_ITEM_FROM_BASKET,
  CLEAR_BASKET,
  UPDATE_SERVICE_QUANTITY,
  UPDATE_PAYMENT_INFO,
  SELECT_LOCALE,
  SELECT_CURRENCY,
  OPEN_MODAL,
  CLOSE_MODAL,
  LOADING_DONE,
  SET_PROMO_CODE,
  CLEAR_PROMO_CODE,
  FILTER_BY_AMENITY,
  REMOVE_FILTER_BY_AMENITY,
  CLEAR_AMENITY_FILTERS,
  FILTER_BY_LOCATION,
  OPEN_UNIT_TYPE_MODAL,
  OPEN_PACKAGE_DEAL_MODAL,
  SEND_SUCCESSFUL_RESERVATION_EVENT,
  SEND_PAYMENT_FORM_INPUT_EVENT,
  ON_OCCUPANCY_CHANGE,
  PRICE_PER_DAY_RESERVATION_CHANGE,
  ACTIVATE_COOKIE_NOTICE,
  ACCEPT_COOKIE_NOTICE,
  SET_BASKET,
  SET_REQUEST,
  SET_REFERENCE_CODE,
  SET_FROM_GOOGLE_TRAVEL
} from 'Rentlio/state/types'
import { fetchAvailability, fetchReservation } from 'Rentlio/state/actions/api'
import { DATE_URL_FORMAT } from 'Rentlio/utils/dateFormatEnum'
import { setQueryParameters } from 'Rentlio/utils/url'
import { childrenAgesForRequest, childrenAgesForUrl } from 'Rentlio/utils/childrenAges'

const getAvailabilityParams = state => {
  const { from, to, adults, children, rooms, unitTypeId, ratePlanId, promoCode } = state.request
  const params = {
    from: from !== null ? moment.unix(from).utc().format(DATE_URL_FORMAT) : null,
    to: to !== null ? moment.unix(to).utc().format(DATE_URL_FORMAT) : null,
    adults: adults !== null ? adults : null,
    children: children !== null ? childrenAgesForRequest(children) : null,
    rooms: rooms !== null ? rooms : null,
    promoCode
  }

  if (unitTypeId !== null) {
    params.unitTypeId = unitTypeId
  }

  if (ratePlanId !== null) {
    params.ratePlanId = ratePlanId
  }

  return params
}

const setAvailabilityQueryParameters = (state, params) =>
  setQueryParameters({ ...params, children: childrenAgesForUrl(state.request.children) })

const getAvailabilityParamsFromOffer = (state, locale) => {
  const { arrivalDate, departureDate, adults } = state

  const params = {
    from: arrivalDate !== null ? moment.unix(arrivalDate).utc().format(DATE_URL_FORMAT) : null,
    to: departureDate !== null ? moment.unix(departureDate).utc().format(DATE_URL_FORMAT) : null,
    adults: adults !== null ? adults : null,
    children: [],
    rooms: 1,
    language: locale
  }

  return params
}

export const updateServiceQuantity = (basketId, serviceId, newQuantity, isMandatoryQuantityUpdate) => ({
  type: UPDATE_SERVICE_QUANTITY,
  basketId,
  serviceId,
  newQuantity,
  isMandatoryQuantityUpdate
})

export const setBasket = basket => ({
  type: SET_BASKET,
  payload: basket
})

export const setRequest = request => (dispatch, getState) => {
  dispatch({
    type: SET_REQUEST,
    payload: request
  })
  const params = getAvailabilityParams(getState())

  setAvailabilityQueryParameters(getState(), params)
  dispatch(fetchAvailability(params))
}

export const removeItemFromBasket = basketId => ({
  type: REMOVE_ITEM_FROM_BASKET,
  basketId
})

export const clearBasket = () => ({
  type: CLEAR_BASKET
})

export const updatePaymentInfo = paymentDetails => ({
  type: UPDATE_PAYMENT_INFO,
  paymentDetails
})

export const clearPromoCode = () => (dispatch, getState) => {
  dispatch({ type: CLEAR_PROMO_CODE })
  const params = getAvailabilityParams(getState())

  setAvailabilityQueryParameters(getState(), params)
  dispatch(fetchAvailability(params))
}

export const setPromoCode = promoCode => (dispatch, getState) => {
  dispatch({ type: SET_PROMO_CODE, promoCode })

  const params = getAvailabilityParams(getState())
  setAvailabilityQueryParameters(getState(), params)
  dispatch(fetchAvailability(params))
}

export const selectLocale = (locale, isCheckIn) => (dispatch, getState) => {
  moment.locale(locale)
  dispatch({
    type: SELECT_LOCALE,
    locale
  })

  const params = getAvailabilityParams(getState())
  const {
    reservation,
    booking: { reservations }
  } = getState()

  if (
    params.from !== null &&
    params.from !== 'Invalid date' &&
    params.to !== null &&
    params.to !== 'Invalid date' &&
    !isCheckIn
  ) {
    dispatch(fetchAvailability(params))
  } else if (reservations && reservations.length > 0) {
    const offerParams = getAvailabilityParamsFromOffer(reservations[0], locale)
    dispatch(fetchAvailability(offerParams))
  }

  if (Object.keys(reservation).length !== 0 && !isCheckIn) {
    dispatch(fetchReservation(reservation.id))
  }
}

export const selectCurrency = currency => dispatch => {
  dispatch({ type: SELECT_CURRENCY, currency })
}

export const openTermsOfServiceModal = () => ({
  type: OPEN_MODAL,
  modalName: modals.TERMS_AND_CONDITIONS
})

export const openPrivacyPolicyModal = () => ({
  type: OPEN_MODAL,
  modalName: modals.PRIVACY_POLICY
})

export const openCancellationPolicyModal = () => ({
  type: OPEN_MODAL,
  modalName: modals.CANCELLATION_POLICY
})

export const openM2Pay3DSModal = data => ({
  type: OPEN_MODAL,
  modalName: modals.M2PAY_3DS,
  data: data
})

export const openSaferpay3DSModal = data => ({
  type: OPEN_MODAL,
  modalName: modals.SAFERPAY_3DS,
  data: data
})

export const closeModal = () => ({
  type: CLOSE_MODAL
})

export const stopLoading = () => ({
  type: LOADING_DONE
})

export const filterByAmenity = amenityId => ({
  type: FILTER_BY_AMENITY,
  amenityId
})

export const filterByLocation = city => ({ type: FILTER_BY_LOCATION, city })

export const removeFilterByAmenity = amenityId => ({
  type: REMOVE_FILTER_BY_AMENITY,
  amenityId
})

export const clearAmenityFilters = () => ({ type: CLEAR_AMENITY_FILTERS })

// This action shouldnt cause any side effect in redux
// we are just dispatching it so we can send equivalent GTM data layer events
export const openUnitTypeModal = data => ({ type: OPEN_UNIT_TYPE_MODAL, payload: data })

// This action shouldnt cause any side effect in redux
// we are just dispatching it so we can send equivalent GTM data layer events
export const openPackageDealModal = data => ({ type: OPEN_PACKAGE_DEAL_MODAL, payload: data })

// Used for GTM and other possible events that need to be sent
export const sendSuccessfulReservationEvent = (internalId, guestDetails) => ({
  type: SEND_SUCCESSFUL_RESERVATION_EVENT,
  guestDetails,
  internalId
})

// Used for GTM and other possible events that need to be sent
export const sendPaymentFormInputEvent = () => ({ type: SEND_PAYMENT_FORM_INPUT_EVENT })

export const onOccupancyChange = (adults, children) => (dispatch, getState) => {
  const state = getState()

  // If VAT is not included, we need to refresh the total rate price.
  // The total rate price retrieved from the backend already includes VAT,
  // and VAT is not provided separately from the rate price,
  // so the total price can't be calculated on the frontend.
  //
  // It's uncommon for properties to have VAT excluded from the price,
  // so we only trigger an API call for those specific users to avoid
  // unnecessary reloads for the rest.
  if (!state.bookingEngine.isVatIncluded) {
    const params = getAvailabilityParams(state)
    dispatch(fetchAvailability({ ...params, adults, children: childrenAgesForRequest(children) }))
  }

  dispatch({
    type: ON_OCCUPANCY_CHANGE,
    adults,
    children
  })
}

export const setPricePerDayReservationIndex = index => ({
  type: PRICE_PER_DAY_RESERVATION_CHANGE,
  index
})

export const activateCookieNotice = isActive => ({
  type: ACTIVATE_COOKIE_NOTICE,
  isActive
})

export const acceptCookieNotice = () => ({
  type: ACCEPT_COOKIE_NOTICE
})

export const setReferenceCode = referenceCode => ({
  type: SET_REFERENCE_CODE,
  referenceCode
})

export const setFromGoogleTravel = fromGoogleTravel => ({
  type: SET_FROM_GOOGLE_TRAVEL,
  fromGoogleTravel
})
